<template>
  <div class="rich-editor-wrap" :style="{ height: height + 'px' }">
    <div v-if="imageUploading" class="upload-mask">
      <i class="el-icon-loading" /> 图片上传中…
    </div>
    <quill-editor
      ref="editor"
      :value="value"
      :options="editorOptions"
      class="rich-editor"
      @change="handleChange"
    />
  </div>
</template>

<script>
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'
import { quillEditor } from 'vue-quill-editor'
import Quill from 'quill'
import { uploadProductImage } from '@/api/product'

const Delta = Quill.import('delta')

const toolbarOptions = [
  [{ header: [1, 2, 3, 4, false] }],
  [{ font: [] }],
  [{ size: ['small', false, 'large', 'huge'] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['blockquote', 'code-block'],
  ['link', 'image'],
  ['clean']
]

export default {
  name: 'RichEditor',
  components: { quillEditor },
  props: {
    value:       { type: String,  default: '' },
    height:      { type: Number,  default: 400 },
    placeholder: { type: String,  default: '请输入内容...' },
    readOnly:    { type: Boolean, default: false }
  },
  data() {
    return {
      imageUploading: false,
      editorOptions: {
        theme: 'snow',
        placeholder: this.placeholder,
        readOnly: this.readOnly,
        modules: {
          toolbar: this.readOnly ? false : toolbarOptions
        }
      }
    }
  },
  mounted() {
    if (!this.readOnly) {
      this.registerImageHandler()
      this.registerPasteHandler()
    }
  },
  methods: {
    getQuill() {
      return this.$refs.editor && this.$refs.editor.quill
    },

    registerImageHandler() {
      const quill = this.getQuill()
      if (!quill) return
      const toolbar = quill.getModule('toolbar')
      toolbar.addHandler('image', () => {
        const input = document.createElement('input')
        input.setAttribute('type', 'file')
        input.setAttribute('accept', 'image/jpeg,image/png,image/webp,image/gif')
        input.click()
        input.onchange = async () => {
          const file = input.files && input.files[0]
          if (!file) return
          this.imageUploading = true
          try {
            const res = await uploadProductImage(file)
            const range = quill.getSelection(true)
            quill.insertEmbed(range ? range.index : 0, 'image', res.data.url)
            if (range) quill.setSelection(range.index + 1)
          } catch (e) {
            this.$message && this.$message.error(e.message || '图片上传失败')
          } finally {
            this.imageUploading = false
          }
        }
      })
    },

    registerPasteHandler() {
      const quill = this.getQuill()
      if (!quill) return

      quill.on('text-change', (delta) => {
        const base64List = []
        ;(delta.ops || []).forEach(op => {
          if (
            op.insert &&
            op.insert.image &&
            typeof op.insert.image === 'string' &&
            op.insert.image.startsWith('data:')
          ) {
            base64List.push(op.insert.image)
          }
        })
        base64List.forEach(base64 => this.replaceBase64WithUrl(base64))
      })
    },

    async replaceBase64WithUrl(base64) {
      const quill = this.getQuill()
      if (!quill) return

      const contents = quill.getContents()
      let idx = 0
      let found = false
      for (const op of contents.ops) {
        if (op.insert && op.insert.image === base64) {
          found = true
          break
        }
        idx += typeof op.insert === 'string' ? op.insert.length : 1
      }
      if (!found) return

      const file = this.base64ToFile(base64)
      this.imageUploading = true
      try {
        const res = await uploadProductImage(file)
        quill.updateContents(
          new Delta().retain(idx).delete(1).insert({ image: res.data.url }),
          'api'
        )
      } catch (e) {
        this.$message && this.$message.error(e.message || '图片上传失败')
        quill.updateContents(new Delta().retain(idx).delete(1), 'api')
      } finally {
        this.imageUploading = false
      }
    },

    base64ToFile(base64) {
      const arr = base64.split(',')
      const mimeMatch = arr[0].match(/:(.*?);/)
      const mime = mimeMatch ? mimeMatch[1] : 'image/png'
      const bstr = atob(arr[1])
      let n = bstr.length
      const u8arr = new Uint8Array(n)
      while (n--) u8arr[n] = bstr.charCodeAt(n)
      const ext = mime.split('/')[1] || 'png'
      return new File([u8arr], `paste-${Date.now()}.${ext}`, { type: mime })
    },

    handleChange({ html }) {
      const content = html === '<p><br></p>' ? '' : (html || '')
      this.$emit('input', content)
    }
  }
}
</script>

<style lang="scss" scoped>
.rich-editor-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  overflow: hidden;
  transition: border-color 0.2s;

  &:hover { border-color: #c0c4cc; }
  &:focus-within { border-color: #409eff; }

  .upload-mask {
    position: absolute;
    inset: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.75);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 14px;
    color: #409eff;
    pointer-events: none;

    i { font-size: 18px; }
  }

  .rich-editor {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    ::v-deep .ql-toolbar {
      border: none;
      border-bottom: 1px solid #e4e7ed;
      background: #fafafa;
      padding: 8px 12px;
      flex-shrink: 0;

      .ql-formats { margin-right: 12px; }

      button:hover, .ql-picker-label:hover,
      button.ql-active, .ql-picker-label.ql-active,
      .ql-picker-item:hover, .ql-picker-item.ql-selected {
        color: #409eff;
        .ql-stroke { stroke: #409eff; }
        .ql-fill   { fill: #409eff; }
      }
    }

    ::v-deep .ql-container {
      border: none;
      flex: 1;
      overflow-y: auto;
      font-size: 14px;
    }

    ::v-deep .ql-editor {
      min-height: 100%;
      padding: 16px 20px;
      line-height: 1.8;
      color: #303133;

      h1 { font-size: 1.8em; }
      h2 { font-size: 1.5em; }
      h3 { font-size: 1.3em; }
      h1, h2, h3, h4 { font-weight: 600; margin: 12px 0 8px; }
      p  { margin-bottom: 8px; }
      ul, ol { padding-left: 20px; }
      blockquote {
        border-left: 4px solid #409eff;
        padding: 8px 16px;
        background: #ecf5ff;
        color: #606266;
        margin: 10px 0;
      }

      img {
        max-width: 100%;
        height: auto;
        border-radius: 2px;
      }
    }

    ::v-deep .ql-editor.ql-blank::before {
      color: #c0c4cc;
      font-style: normal;
    }
  }
}
</style>
